import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/RateReview';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import axios from 'axios';

const baseURL = process.env.REACT_APP_BACKEND_URL;

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const token = localStorage.getItem('access_token');

  const handleSubmit = async () => {
    if (!summary.trim()) return;
    try {
      setSubmitting(true);
      const res = await axios.post(`${baseURL}feedback`, {
        summary: summary.trim(),
        description: description.trim(),
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setAlert({
        open: true,
        message: `Thank you! Ticket ${res.data.ticket} created.`,
        severity: 'success',
      });
      setOpen(false);
      setSummary('');
      setDescription('');
    } catch (err) {
      setAlert({
        open: true,
        message: 'Could not submit feedback. Please try again.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          right: -32,
          top: '50%',
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
          zIndex: 1000,
          backgroundColor: '#164A1A',
          color: '#F9F5EB',
          fontFamily: 'MADE TOMMY',
          fontWeight: 500,
          fontSize: '12px',
          textTransform: 'none',
          borderRadius: '8px 8px 0 0',
          px: 2,
          py: 0.5,
          minWidth: 'auto',
          '&:hover': { backgroundColor: '#1d5e22' },
        }}
        startIcon={<FeedbackIcon sx={{ fontSize: '14px !important', transform: 'rotate(90deg)' }} />}
      >
        Feedback
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'MADE TOMMY', fontWeight: 600, color: '#164A1A' }}>
          Send Feedback
          <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Report a bug, suggest an improvement, or share your thoughts.
          </Typography>
          <TextField
            fullWidth
            label="Subject"
            variant="outlined"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            fullWidth
            label="Details (optional)"
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#666' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !summary.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <FeedbackIcon />}
            sx={{
              backgroundColor: '#164A1A',
              '&:hover': { backgroundColor: '#1d5e22' },
              textTransform: 'none',
            }}
          >
            {submitting ? 'Sending...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={alert.open}
        autoHideDuration={5000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          variant="filled"
          sx={alert.severity === 'success' ? { backgroundColor: '#EAFFD8', color: '#164A1A' } : {}}
          iconMapping={{ success: <CheckCircleOutlineRoundedIcon fontSize="inherit" /> }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackButton;
